import React from "react";
import { Card, CardContent, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import GetAppIcon from "@material-ui/icons/GetApp";
import DescriptionIcon from "@material-ui/icons/Description";
import ImageIcon from "@material-ui/icons/Image";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";

const useStyles = makeStyles((theme) => ({
  attachmentCard: {
    marginBottom: theme.spacing(2),
  },
  attachmentTable: {
    width: "100%",
    borderCollapse: "collapse",
  },
  attachmentRow: {
    borderBottom: "1px solid #e0e0e0",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  attachmentTitle: {
    padding: theme.spacing(1),
    fontWeight: "bold",
    width: "40%",
    verticalAlign: "top",
  },
  attachmentDetail: {
    padding: theme.spacing(1),
    width: "60%",
  },
  attachmentLink: {
    display: "flex",
    alignItems: "center",
    textDecoration: "none",
    color: theme.palette.primary.main,
    padding: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  attachmentIcon: {
    marginRight: theme.spacing(1),
    fontSize: "1.2rem",
  },
  noAttachments: {
    fontStyle: "italic",
    color: theme.palette.text.secondary,
    padding: theme.spacing(2),
    textAlign: "center",
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "0.8rem",
  },
}));

const ATTACHMENT_LABELS = {
  poa_proof: "დოკუმენტები",
  elevator_absence_reason: "დოკუმენტები",
  building_permit: "დოკუმენტები",

  single_mother_certificate: "დოკუმენტები",
  children_birth_certificate: "დოკუმენტები",
  spouse_death_certificate: "დოკუმენტები",
  child_adoption_certificate: "დოკუმენტები",

  agro_property_proof: "დოკუმენტები",
  agro_animal_proof: "დოკუმენტები",
  agro_farm_proof: "დოკუმენტები",

  eco_authentic_proof: "დოკუმენტები",

  schedule_payments: "დოკუმენტები",
  monitoring_attachments: "დოკუმენტები",

  internal_attachments: "დოკუმენტები",
  documents: "დოკუმენტები",
};

const getFileIcon = (fileName, mimeType) => {
  if (!fileName && !mimeType) return <DescriptionIcon />;

  const extension = fileName ? fileName.split(".").pop().toLowerCase() : "";

  if (mimeType?.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp"].includes(extension)) {
    return <ImageIcon />;
  }

  if (mimeType === "application/pdf" || extension === "pdf") {
    return <PictureAsPdfIcon />;
  }

  return <DescriptionIcon />;
};

const getFileName = (filePath) => {
  if (!filePath) return "ფაილი";

  const pathParts = filePath.split("/");
  const fileName = pathParts[pathParts.length - 1];

  if (/^\d+-\d+\.\w+$/.test(fileName)) {
    return "ფაილი";
  }

  return fileName || "ფაილი";
};

const AttachmentViewer = ({ attachments, title = "მიმაგრებული ფაილები", classes: externalClasses, showEmptyMessage = true }) => {
  const classes = useStyles();

  const tableClass = externalClasses?.table || classes.attachmentTable;
  const rowClass = externalClasses?.row || classes.attachmentRow;
  const titleClass = externalClasses?.title || classes.attachmentTitle;
  const detailClass = externalClasses?.detail || classes.attachmentDetail;
  const cardSummaryClass = externalClasses?.cardSummary;
  const componentClass = externalClasses?.component;

  if (!attachments || typeof attachments !== "object") {
    if (!showEmptyMessage) return null;

    return (
      <Card className={componentClass || classes.attachmentCard} variant="outlined">
        <Typography className={cardSummaryClass}>{title}</Typography>
        <CardContent>
          <div className={classes.noAttachments}>დანართები არ არის მიმაგრებული</div>
        </CardContent>
      </Card>
    );
  }

  const attachmentRows = [];

  Object.entries(attachments).forEach(([fieldName, fieldValue]) => {
    if (!fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0)) {
      return;
    }

    const label = ATTACHMENT_LABELS[fieldName] || fieldName;

    if (Array.isArray(fieldValue)) {
      fieldValue.forEach((attachment, index) => {
        if (!attachment || attachment === null || attachment === undefined) return;

        let fileName, signedUrl, originalPath, error;

        if (typeof attachment === "string") {
          originalPath = attachment;
          fileName = getFileName(attachment);
          signedUrl = null;
        } else if (typeof attachment === "object" && attachment !== null) {
          originalPath = attachment.original_path;
          signedUrl = attachment.signed_url;
          fileName = getFileName(attachment.original_path);
          error = attachment.error;
        } else {
          return;
        }

        if (originalPath || signedUrl) {
          const displayLabel = fieldValue.filter((item) => item !== null && item !== undefined).length > 1 ? `${label} (${index + 1})` : label;

          attachmentRows.push({
            label: displayLabel,
            fileName,
            signedUrl,
            originalPath,
            error,
            key: `${fieldName}-${index}`,
          });
        }
      });
    } else if (typeof fieldValue === "object" && fieldValue.signed_url) {
      attachmentRows.push({
        label,
        fileName: getFileName(fieldValue.original_path),
        signedUrl: fieldValue.signed_url,
        originalPath: fieldValue.original_path,
        error: fieldValue.error,
        key: fieldName,
      });
    }
  });

  if (attachmentRows.length === 0) {
    if (!showEmptyMessage) return null;

    return (
      <Card className={componentClass || classes.attachmentCard} variant="outlined">
        <Typography className={cardSummaryClass}>{title}</Typography>
        <CardContent>
          <div className={classes.noAttachments}>დანართები არ არის მიმაგრებული</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={componentClass || classes.attachmentCard} variant="outlined">
      <Typography className={cardSummaryClass}>{title}</Typography>
      <CardContent>
        <table className={tableClass}>
          <tbody>
            {attachmentRows.map((row) => (
              <tr key={row.key} className={rowClass}>
                <td className={titleClass}>{row.label}:</td>
                <td className={detailClass}>
                  {row.error ? (
                    <div>
                      <div className={classes.errorText}>ფაილის ჩატვირთვა ვერ მოხერხდა: {row.error}</div>
                      <div style={{ fontSize: "0.8rem", color: "#666" }}>{row.originalPath}</div>
                    </div>
                  ) : row.signedUrl ? (
                    <a href={row.signedUrl} target="_blank" rel="noopener noreferrer" className={classes.attachmentLink}>
                      {getFileIcon(row.fileName)}
                      <span>{row.fileName}</span>
                      <GetAppIcon className={classes.attachmentIcon} style={{ marginLeft: "auto" }} />
                    </a>
                  ) : (
                    <div className={classes.errorText}>ფაილი მიუწვდომელია</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};

export default AttachmentViewer;
